import { Fieldset, Label } from '@headlessui/react'
import { MediaField } from '../../components/forms/MediaField'
import * as Sentry from '@sentry/react'
import { SingleTextField } from '../../components/forms/SingleTextField'
import { DatePickerField } from '../../components/forms/DatePickerField'
import { ImageActionButton } from '../../components/buttons/ImageActionButton'
import Logout from '../../assets/logout.svg?react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../data/useAuthStore'
import { useAuth } from '../../pocketbase/useAuth'
import { useUpdateCurrentUser } from '../../queries/useUser'
import { getUserAvatarUrl } from '../../api/users'
import { SaveActionButton } from '../../components/buttons/SaveActionButton'

export const ProfileView = () => {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const { user, userProfile } = useAuthStore()
  const isAdmin = useAuthStore((state) => state.isAdmin)
  const updateUserMutation = useUpdateCurrentUser()

  // Local editable state
  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')
  const [gender, setGender] = useState('')
  const [birthday, setBirthday] = useState<Date | null>(null)
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null)
  const [existingAvatarUrl, setExistingAvatarUrl] = useState<string | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [usernameError, setUsernameError] = useState<string | null>(null)

  useEffect(() => {
    if (userProfile) {
      setFullName(userProfile.fullName || '')
      setUsername(userProfile.username || '')
      setGender(userProfile.gender || '')
      setBirthday(userProfile.birthday ? new Date(userProfile.birthday) : null)

      // Set existing avatar URL if user has one
      if (userProfile.avatar) {
        const avatarUrl = getUserAvatarUrl({ id: userProfile.id, avatar: userProfile.avatar })
        setExistingAvatarUrl(avatarUrl)
      } else {
        setExistingAvatarUrl(null)
      }
    }
  }, [userProfile])

  const onImageSelected = (file: File) => {
    setSelectedAvatar(file)
  }

  const formatMemberSince = (): string => {
    const created = user?.created
    try {
      if (created != null) {
        const d: Date = new Date(created)
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
      }
    } catch { }
    return ''
  }

  const onLogoutClick = async () => {
    await logout()
    navigate('/')
  }

  const onSaveClick = () => {
    // Clear any previous errors
    setUsernameError(null)

    updateUserMutation.mutate(
      {
        fullName,
        username,
        gender,
        birthday,
        // Only include avatar if user selected a new one
        ...(selectedAvatar && { avatar: selectedAvatar }),
      },
      {
        onSuccess: () => {
          // Reset selected avatar after successful save
          setSelectedAvatar(null)
          // Show success message
          setShowSuccess(true)
          setTimeout(() => setShowSuccess(false), 3000)
        },
        onError: (error: any) => {
          console.error('Failed to save profile', error)
          Sentry.captureException(error)

          // Check for username validation errors
          if (error?.data?.data?.username) {
            const errorCode = error.data.data.username.code

            if (errorCode === 'username_already_taken') {
              setUsernameError('Username is already taken')
            } else if (errorCode === 'username_forbidden_word') {
              setUsernameError("Username isn't valid, please choose another")
            }
          }
        },
      }
    )
  }

  const isDirty = (() => {
    if (!userProfile) return false
    // Avatar change counts as dirty
    if (selectedAvatar) return true
    const docBirthday = userProfile.birthday ? new Date(userProfile.birthday) : null
    const sameBirthday = (docBirthday?.toISOString() ?? '') === (birthday?.toISOString() ?? '')
    const sameFullName = (userProfile.fullName || '') === fullName
    const sameUsername = (userProfile.username || '') === username
    const sameGender = (userProfile.gender || '') === gender
    return !(sameFullName && sameUsername && sameGender && sameBirthday)
  })()

  return (
    <div>
      <section className={'lg:w-2/3 w-full md:p-10 p-5'}>
        <form className={'space-y-6'}>
          <Fieldset className={'md:space-y-8 space-y-6 bg-white'}>
            <MediaField
              label={'Profile image'}
              onImageSelected={onImageSelected}
              initialImageUrl={existingAvatarUrl}
            />

            <SingleTextField
              label={'Full name'}
              placeholder={'Add a name'}
              value={fullName}
              onChange={setFullName}
              maxLength={80}
            />

            <SingleTextField
              label={'User name'}
              placeholder={'Add a name'}
              value={username}
              onChange={setUsername}
              maxLength={80}
            />

            <SingleTextField
              label={'Email'}
              placeholder={'Add a name'}
              value={user?.email ?? ''}
              onChange={() => { }}
              disabled
            />

            <SingleTextField
              label={'Gender'}
              placeholder={'Add a gender'}
              value={gender}
              onChange={setGender}
              maxLength={80}
            />
            <DatePickerField
              label={'Birthday'}
              value={birthday}
              onChange={setBirthday}
            />

            <div className="flex items-center gap-4">
              <SaveActionButton label={'Save profile'} onClick={onSaveClick} disabled={!isDirty} />
              {showSuccess && (
                <span className="text-title text-sm">Profile saved successfully!</span>
              )}
              {usernameError && (
                <p className="mt-2 text-sm text-red-600">{usernameError}</p>
              )}
            </div>

            <Label className={'text-lg'}>{`Member since: ${formatMemberSince()}`}</Label>
            <ImageActionButton label={'Log out'} icon={<Logout width={20} height={20} />} onClick={onLogoutClick} />
            {isAdmin && <Label>{`v${__APP_VERSION__}`}</Label>}
          </Fieldset>
        </form>
      </section>
    </div>
  )
}
